<template>
    <div class="attendance-history animated fadeIn">
        <b-overlay :show="loading" rounded="sm" spinner-variant="primary" class="h-100">
            <div class="glass-container">
                <!-- Header Card -->
                <div class="glass-header mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <b-button 
                                variant="link" 
                                class="text-white p-0 mr-3"
                                @click="$router.go(-1)"
                            >
                                <i class="fa fa-arrow-left fa-lg"></i>
                            </b-button>
                            <div>
                                <h5 class="mb-0 text-white font-w700">{{ $translate('activity_history') }}</h5>
                                <small class="text-white-op-70">{{ $translate('list_of_past_and_upcoming_activities') }}</small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- List Card -->
                <div class="glass-card">
                    <div class="p-0">
                        <b-table
                            v-if="activities.length > 0"
                            :items="activities"
                            :fields="tableFields"
                            responsive
                            hover
                            selectable
                            select-mode="single"
                            class="modern-table clickable-rows"
                            thead-class="d-none"
                            @row-clicked="viewAttendees"
                        >
                            <template #cell(description)="data">
                                <div class="py-2">
                                    <div class="font-w600 text-dark mb-1">{{ data.item.description || data.item.reference_no }}</div>
                                    <div class="small text-muted">
                                        <i class="fa fa-calendar-o mr-1"></i> {{ data.item.schedule_at | filterLocalDateTime }}
                                    </div>
                                </div>
                            </template>

                            <template #cell(status)="data">
                                <div class="text-right">
                                    <b-badge :variant="getStatusVariant(data.item)" class="mb-1">
                                        {{ getStatusLabel(data.item) }}
                                    </b-badge>
                                </div>
                            </template>

                            <template #cell(actions)="data">
                                <div class="d-flex justify-content-end align-items-center">
                                    <b-button
                                        variant="outline-primary"
                                        size="sm"
                                        class="btn-icon-sm"
                                        @click="viewAttendees(data.item)"
                                        v-b-tooltip.hover
                                        :title="$translate('view_attendees')"
                                    >
                                        <i class="fa fa-users"></i>
                                    </b-button>
                                </div>
                            </template>
                        </b-table>

                        <div v-else class="text-center py-5">
                            <div class="empty-state-icon mb-3">
                                <i class="fa fa-calendar-times-o"></i>
                            </div>
                            <h5 class="text-muted mb-2">{{ $translate('no_activities_found') }}</h5>
                            <p class="text-muted small">{{ $translate('no_history_available') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </b-overlay>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'AttendanceHistory',
    data() {
        return {
            loading: false,
            activities: [],
            orderTypeId: null,
            configMode: 'check_in_only'
        }
    },
    computed: {
        tableFields() {
            return [
                { key: 'description', label: this.$translate('activity'), sortable: false },
                { key: 'status', label: this.$translate('status'), sortable: false, tdClass: 'text-right' },
                { key: 'actions', label: '', sortable: false, tdClass: 'text-right' }
            ]
        }
    },
    created() {
        this.orderTypeId = this.$route.params.orderTypeId
        this.fetchActivities()
    },
    methods: {
        async fetchActivities() {
            if (!this.orderTypeId) return

            this.loading = true
            try {
                const config = await this.$store.dispatch('attendance/fetchConfigByOrderType', this.orderTypeId)
                if (config) {
                    this.configMode = config.mode
                }

                const data = await this.$store.dispatch('orders/fetchDirect', {
                    page: 1,
                    query: '',
                    filters: { type: this.orderTypeId }
                })
                
                this.activities = data || []
            } catch (error) {
                this.$bvToast.toast(this.$translate('error_fetching_activities') || 'Error fetching activities', {
                    title: this.$translate('error') || 'Error',
                    variant: 'danger',
                    solid: true
                })
            } finally {
                this.loading = false
            }
        },
        viewAttendees(item) {
            this.$router.push({
                name: 'AttendanceAttendees',
                params: { orderId: item.id },
                query: { mode: this.configMode }
            })
        },
        getStatusVariant(item) {
            if (item.cancelled) return 'danger'
            if (item.completed) return 'success'
            if (item.released) return 'primary'
            return 'secondary'
        },
        getStatusLabel(item) {
            if (item.cancelled) return this.$translate('cancelled')
            if (item.completed) return this.$translate('completed')
            if (item.released) return this.$translate('released')
            return this.$translate('pending')
        }
    },
    filters: {
        filterLocalDateTime(value) {
            if (!value) return ''
            return moment.utc(value).local().format('DD MMM YYYY, hh:mm A')
        }
    }
}
</script>

<style scoped>
.attendance-history {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    padding: 20px 15px;
    font-family: 'Inter', sans-serif;
}

.glass-container {
    max-width: 800px;
    margin: 0 auto;
}

.glass-header {
    background: linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 20px;
    border: none;
}

.glass-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
    overflow: hidden;
}

.text-white-op-70 {
    color: rgba(255,255,255,0.7);
}

.modern-table {
    margin-bottom: 0;
}

.modern-table ::v-deep td {
    padding: 15px 20px;
    vertical-align: middle;
    border-top: 1px solid #f0f2f5;
}

.modern-table ::v-deep tr:first-child td {
    border-top: none;
}

.btn-icon-sm {
    width: 32px;
    height: 32px;
    padding: 0;
    line-height: 30px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.empty-state-icon {
    width: 70px;
    height: 70px;
    background: #f8f9fa;
    color: #d1d3df;
    border-radius: 50%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
}

.clickable-rows ::v-deep tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.clickable-rows ::v-deep tbody tr:hover {
    background-color: #f0f7ff;
}
</style>
